import React, { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, LogIn, Mail, Send } from "lucide-react";
import { API_BASE_URL } from "../config";

export default function ForgotPasswordForm({ embedded = false }) {
  const [email, setEmail] = useState("");
  const [feedback, setFeedback] = useState(null); // { type: "success" | "error", text: string }
  const [loading, setLoading] = useState(false);

  const fieldWrapperClasses = `flex items-center gap-3 rounded-2xl border border-slate-200 ${
    embedded ? "bg-white" : "bg-white/95"
  } px-4 py-3 text-sm text-slate-900 shadow-inner shadow-white/40 transition focus-within:border-slate-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-slate-400`;

  const inputClasses =
    "flex-1 border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0";

  const buttonClasses =
    "inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/25 transition-colors duration-200 transition-transform hover:-translate-y-1 hover:scale-105 hover:from-slate-800 hover:to-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60";

  const feedbackClasses = feedback
    ? `rounded-2xl border px-4 py-3 text-sm text-center ${
        feedback.type === "error"
          ? "border-rose-300 bg-rose-50 text-rose-600"
          : "border-emerald-300 bg-emerald-50 text-emerald-700"
      }`
    : "";

  async function onSubmit(event) {
    event.preventDefault();
    setLoading(true);
    setFeedback(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/password-reset/request/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Failed to send the email.");
      setFeedback({
        type: "success",
        text: "If the account exists, we sent an email with instructions.",
      });
      setEmail("");
    } catch (error) {
      setFeedback({
        type: "error",
        text: error.message || "We couldn't send the reset email. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  const form = (
    <form onSubmit={onSubmit} className="space-y-5" noValidate>
      <label className="text-sm font-medium text-slate-600">
        <span className="sr-only">Email</span>
        <div className={fieldWrapperClasses}>
          <Mail size={20} className="text-slate-400" />
          <input
            type="email"
            required
            placeholder="name@company.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={inputClasses}
          />
        </div>
      </label>
      <button className={buttonClasses} disabled={loading}>
        {loading ? (
          "Sending..."
        ) : (
          <>
            <Send className="h-4 w-4" />
            Send reset link
          </>
        )}
      </button>
      {feedback ? (
        <div
          className={feedbackClasses}
          role={feedback.type === "error" ? "alert" : "status"}
          aria-live="polite"
        >
          {feedback.text}
        </div>
      ) : null}
    </form>
  );

  if (embedded) {
    return form;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="absolute inset-0">
        <div className="pointer-events-none absolute -top-40 left-24 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
        <div className="pointer-events-none absolute bottom-16 right-10 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
      </div>

      <div className="relative flex min-h-screen items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-white/15 bg-white/95 p-8 text-slate-900 shadow-[0_35px_120px_-45px_rgba(15,23,42,0.8)] backdrop-blur-2xl sm:p-10">
            <div className="space-y-6">
              <div className="space-y-3 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                  <KeyRound size={28} />
                </div>
                <div className="space-y-2">
                  <h1 className="text-3xl font-semibold text-slate-900">Reset your password</h1>
                  <p className="text-sm text-slate-500">
                    Enter the email linked to your account and we will send you a secure reset link.
                  </p>
                </div>
              </div>

              {form}

              <div className="text-center text-sm text-slate-500">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-900 underline-offset-4 transition hover:text-slate-700 hover:underline"
                >
                  <LogIn size={18} />
                  Back to sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
